import { useState } from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import CheckIcon from "@mui/icons-material/Check";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { api } from "services/api.service";
import { useAlert } from "hooks/useAlert";
import { JobDetailViewModel } from "./jobDetailTypes";
import { DetailCard } from "./DetailCard";

type Props = {
  job: JobDetailViewModel;
  onChanged?: () => void;
};

export default function ActionsCard({ job, onChanged }: Props) {
  const [submitting, setSubmitting] = useState<"advance" | "revert" | null>(null);
  const { alertSuccess, alertError } = useAlert();

  if (!job.nextStageLabel && !job.previousStageLabel && !job.isComplete) {
    return null;
  }

  const handleAdvance = () => {
    setSubmitting("advance");

    api
      .post(`/logistics_tickets/${job.ticketUuid}/advance`)
      .then(() => {
        alertSuccess(`Advanced to ${job.nextStageLabel}`);
        onChanged?.();
      })
      .catch(() => {
        alertError("Unable to advance this pickup. Please try again.");
      })
      .finally(() => {
        setSubmitting(null);
      });
  };

  const handleRevert = () => {
    setSubmitting("revert");

    api
      .post(`/logistics_tickets/${job.ticketUuid}/revert`)
      .then(() => {
        alertSuccess(`Moved back to ${job.previousStageLabel}`);
        onChanged?.();
      })
      .catch(() => {
        alertError("Unable to move this pickup back. Please try again.");
      })
      .finally(() => {
        setSubmitting(null);
      });
  };

  return (
    <DetailCard title="Actions">
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
        {job.isComplete ? (
          <Button
            variant="contained"
            fullWidth
            disabled
            startIcon={<CheckIcon />}
            sx={{
              py: 1.25,
              textTransform: "uppercase",
              fontWeight: 700,
              fontSize: 12,
              letterSpacing: 0.4,
              backgroundColor: "#43A047",
              color: "#fff",
              "&.Mui-disabled": {
                backgroundColor: "#43A047",
                color: "#fff",
                opacity: 1,
              },
            }}
          >
            Pickup Complete
          </Button>
        ) : (
          job.nextStageLabel && (
            <Button
              variant="contained"
              fullWidth
              disabled={submitting !== null}
              onClick={handleAdvance}
              startIcon={submitting === "advance" ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              sx={{
                py: 1.25,
                textTransform: "uppercase",
                fontWeight: 700,
                fontSize: 12,
                letterSpacing: 0.4,
              }}
            >
              Advance to: {job.nextStageLabel}
            </Button>
          )
        )}

        {job.previousStageLabel && (
          <Button
            variant="outlined"
            fullWidth
            disabled={submitting !== null}
            onClick={handleRevert}
            startIcon={submitting === "revert" ? <CircularProgress size={16} color="inherit" /> : <ChevronLeftIcon />}
            sx={{
              py: 1.25,
              textTransform: "uppercase",
              fontWeight: 700,
              fontSize: 12,
              letterSpacing: 0.4,
            }}
          >
            Back to: {job.previousStageLabel}
          </Button>
        )}
      </Box>
    </DetailCard>
  );
}
